import { BaseScene } from '@core/scenes/BaseScene';
import { Palette, SceneKeys } from '@core/config/constants';
import { RhythmConfig } from '@modules/rhythm/config';
import { AdventureConfig } from '@modules/adventure/config';
import { TriviaConfig } from '@modules/trivia/config';
import { MemoryConfig } from '@modules/memory/config';

/** Un logro y si el jugador ya lo consiguio. */
interface Achievement {
  readonly emoji: string;
  readonly name: string;
  readonly description: string;
  readonly unlocked: boolean;
}

const MAX_CARD_WIDTH = 260;

/**
 * Pantalla de logros: muestra metas (puntajes, estrellas, accesorios) y cuales ya se
 * cumplieron. Se calcula a partir del perfil del jugador, sin estado extra.
 */
export class AchievementsScene extends BaseScene {
  public constructor() {
    super(SceneKeys.Achievements);
  }

  public create(): void {
    this.initResponsive();
    this.addSelectedBackground();
    this.addTwinkles(18);

    this.addBackButton();

    const achievements = this.buildAchievements();
    const unlocked = achievements.filter((achievement) => achievement.unlocked).length;

    this.add
      .text(this.centerX, this.scale.height * 0.07, '🏆 Logros', {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '46px',
        color: Palette.textLight,
      })
      .setOrigin(0.5);

    this.add
      .text(this.centerX, this.scale.height * 0.07 + 42, `Conseguiste ${unlocked} de ${achievements.length}`, {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '22px',
        color: '#4dd0e1',
      })
      .setOrigin(0.5);

    this.renderGrid(achievements, this.scale.height * 0.2);
  }

  private buildAchievements(): Achievement[] {
    const coins = this.profile.coins;
    return [
      { emoji: '⭐', name: 'Primera estrella', description: 'Conseguí tu primera estrella', unlocked: coins >= 1 },
      { emoji: '💰', name: 'Coleccionista', description: 'Juntá 50 estrellas', unlocked: coins >= 50 },
      { emoji: '👑', name: 'Súper estrella', description: 'Juntá 200 estrellas', unlocked: coins >= 200 },
      {
        emoji: '🎵',
        name: 'Bailarina',
        description: '100 puntos en Ritmo Pop',
        unlocked: this.profile.getHighScore(RhythmConfig.key) >= 100,
      },
      {
        emoji: '🏃',
        name: 'Exploradora',
        description: '100 puntos en Aventura',
        unlocked: this.profile.getHighScore(AdventureConfig.key) >= 100,
      },
      {
        emoji: '🧠',
        name: 'Sabihonda',
        description: '30 puntos en Trivia',
        unlocked: this.profile.getHighScore(TriviaConfig.key) >= 30,
      },
      {
        emoji: '🎴',
        name: 'Memoriosa',
        description: '100 puntos en Memoria',
        unlocked: this.profile.getHighScore(MemoryConfig.key) >= 100,
      },
      {
        emoji: '🛍️',
        name: 'Fashionista',
        description: 'Comprá un accesorio en la tienda',
        unlocked: this.profile.ownedItems.length > 0,
      },
      {
        emoji: '🌟',
        name: 'Tu estrella',
        description: 'Elegí tu personaje',
        unlocked: this.profile.character !== '',
      },
    ];
  }

  private renderGrid(achievements: Achievement[], gridTop: number): void {
    const { width, height } = this.scale;
    const gridBottom = height - 48;
    const gridAreaHeight = Math.max(160, gridBottom - gridTop);

    const gap = Math.max(14, width * 0.018);
    const idealColumnWidth = 230;
    let columns = Math.max(1, Math.floor((width * 0.94 + gap) / (idealColumnWidth + gap)));
    columns = Math.min(columns, achievements.length, 4);
    if (this.isPortrait) {
      columns = Math.min(columns, 2);
    }
    const rows = Math.ceil(achievements.length / columns);

    const cardWidth = Math.min(MAX_CARD_WIDTH, (width * 0.94 - (columns - 1) * gap) / columns);
    const cardHeight = Math.min(cardWidth * 0.62, (gridAreaHeight - (rows - 1) * gap) / rows);

    const totalHeight = rows * cardHeight + (rows - 1) * gap;
    const startY = gridTop + (gridAreaHeight - totalHeight) / 2 + cardHeight / 2;

    achievements.forEach((achievement, index) => {
      const column = index % columns;
      const row = Math.floor(index / columns);
      const cardsInRow = Math.min(columns, achievements.length - row * columns);
      const rowWidth = cardsInRow * cardWidth + (cardsInRow - 1) * gap;
      const startX = this.centerX - rowWidth / 2 + cardWidth / 2;

      const x = startX + column * (cardWidth + gap);
      const y = startY + row * (cardHeight + gap);
      this.createCard(achievement, x, y, cardWidth, cardHeight, index * 50);
    });
  }

  private createCard(
    achievement: Achievement,
    x: number,
    y: number,
    cardWidth: number,
    cardHeight: number,
    entranceDelay: number,
  ): void {
    const container = this.add.container(x, y);

    const background = this.add.graphics();
    background.fillStyle(achievement.unlocked ? Palette.secondary : Palette.backgroundAlt, achievement.unlocked ? 1 : 0.7);
    background.fillRoundedRect(-cardWidth / 2, -cardHeight / 2, cardWidth, cardHeight, 18);
    background.lineStyle(4, Palette.highlight, achievement.unlocked ? 0.9 : 0);
    background.strokeRoundedRect(-cardWidth / 2, -cardHeight / 2, cardWidth, cardHeight, 18);

    const emojiSize = Math.round(cardHeight * 0.42);
    const emoji = this.add
      .text(-cardWidth / 2 + emojiSize * 0.7, 0, achievement.unlocked ? achievement.emoji : '🔒', {
        fontSize: `${emojiSize}px`,
      })
      .setOrigin(0.5);

    const textX = -cardWidth / 2 + emojiSize * 1.3;
    const textWidth = cardWidth / 2 + (cardWidth / 2 - emojiSize * 1.3) - 12;

    const name = this.add
      .text(textX, -cardHeight * 0.16, achievement.name, {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '21px',
        color: achievement.unlocked ? Palette.textLight : '#b8a9d9',
        wordWrap: { width: textWidth },
      })
      .setOrigin(0, 0.5);

    const description = this.add
      .text(textX, cardHeight * 0.2, achievement.description, {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '15px',
        color: achievement.unlocked ? '#e8dcff' : '#8a7bb0',
        wordWrap: { width: textWidth },
      })
      .setOrigin(0, 0.5);

    container.add([background, emoji, name, description]);
    container.setSize(cardWidth, cardHeight);

    container.setScale(0.8).setAlpha(0);
    this.tweens.add({
      targets: container,
      scale: 1,
      alpha: 1,
      duration: 260,
      delay: entranceDelay,
      ease: 'Back.easeOut',
    });
  }
}
